import React, { useEffect, useLayoutEffect, useState, useRef, forwardRef } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { openModal } from '../../redux/Slices/modalSlice';
import { selectBoardId, setBoardId } from '../../redux/Slices/boardIdSlice';
import { fetchBoardMembers } from '../../redux/Slices/boardMembersSlice';
import { togglePopover } from '../../redux/Slices/popoverSlice';
import ImagePlaceholder from '../Global/ImagePlaceholder';
import MemberImage from '../Global/MemberImage';
import { initialFiltersState } from '../../utils/helpers';
import { fetchWorkspaces } from '../../redux/Slices/workspaceSlice';
import { config } from '../../config';
import useOutsideClick from '../../hooks/useOutsideClick';
import Filter from './Filter';
import { setFilterQuery } from '../../redux/Slices/filterQuerySlice';
import { fetchMembers } from '../../redux/Slices/memberSlice';
import BoardMenu from '../BoardMenu/BoardMenu';
import NavbarLeft from './NavbarLeft';
import MoreMembersCount from '../Global/MoreMembersCount';
import { setBoardBackground, setSelectedBackground, updateBoardMembers } from '../../redux/Slices/boardSlice';
import PersonAdd from '../icons/PersonAdd';
import MoreHoriz from '../icons/MoreHoriz';
import Monitoring from '../icons/Monitoring';
import WandShine from '../icons/WandShine';
import Bolt from '../icons/Bolt';
import useHandlePopoverClick from '../../hooks/useHandlePopoverClick';
import FilterList from '../icons/FilterList';
import KeyboardArrowDown from '../icons/KeyboardArrowDown';
import { BOARD_VIEWS, VIEW_ORDER } from './viewRegistry';
import useBoardPermissions from '../../hooks/useBoardPermissions';
import { useSocketAction } from '../../hooks/useSocketManager';
import { ActionTypes } from '../../sockets/ActionTypes';

const Navbar = forwardRef(({ cardCount, setCardCount, board, setBoard, isReadOnly = false, viewMode, setViewMode }, ref) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const filterRef = useRef();
    const filterPopoverRef = useRef();
    const menuRef = useRef();
    const viewMenuRef = useRef();

    const boardId = useSelector(selectBoardId);
    const [isShowFilter, setShowFilter] = useState(false);
    // Which side the filter popover opens toward. Default left-anchored so it
    // opens to the right of the filter icon; flips to right-anchored only when
    // it would overflow the right edge of the viewport.
    const [filterAlign, setFilterAlign] = useState('left');
    const [isShowMenu, setShowMenu] = useState(false);
    const [isViewMenuOpen, setViewMenuOpen] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [isChatbotOpen, setIsChatbotOpen] = useState(false);

    const { handlePopoverClick } = useHandlePopoverClick();

    // Get board permissions
    const { isAdmin, canManageMembers } = useBoardPermissions(board);


    const location = useLocation();

    const members = useSelector(state => state.member.boardMembers);
    const boardData = useSelector(state => state.board);
    const user = useSelector((state) => state.user);

    const query = useSelector((state) => state.filterQuery);

    // Real-time socket listener for BOARD member changes
    // NOTE: This should NOT handle card member events (which also use MEMBER_ADDED/MEMBER_REMOVED)
    useSocketAction([
        ActionTypes.MEMBER_ADDED,
        ActionTypes.MEMBER_REMOVED,
        ActionTypes.ROLE_UPDATED,
    ], async (data) => {
        // Skip events triggered by current user (already handled optimistically)
        if (data.reqUser === user?.user?._id) return;

        // IMPORTANT: Only process BOARD member events, not CARD member events
        // Card member events have a 'card' property, board member events have 'typeId' or 'members'
        if (data.card) {
            // This is a card member event, ignore it
            return;
        }

        // Refresh members when member is added/removed or role is updated on this board
        if (data.typeId === boardId || data.boardId === boardId) {
            console.log('[Navbar] Real-time BOARD member event:', data.action, data);

            // Use members from socket event if available, otherwise fetch
            let updatedMembers = data.members;
            if (!updatedMembers) {
                updatedMembers = await dispatch(fetchMembers({ type: 'board', id: boardId })).unwrap();
            }

            console.log('[Navbar] Updated members structure:', updatedMembers?.[0]);

            // Update Redux board state with new members
            dispatch(updateBoardMembers(updatedMembers));

            // Update board context with new members to trigger permission recalculation
            // IMPORTANT: Ensure we maintain the board object structure to prevent read-only mode flickering
            if (updatedMembers && Array.isArray(updatedMembers) && setBoard) {
                setBoard((prevBoard) => {
                    if (!prevBoard) return prevBoard;

                    return {
                        ...prevBoard,
                        members: updatedMembers
                    };
                });
            }
        }
    }, [dispatch, boardId, user?.user?._id, setBoard]);

    const handleShareBoardModal = () => {
        dispatch(openModal({ modalType: 'SHARE_BOARD', modalData: {} }));
    };

    const handleAnalyticsClick = () => {
        navigate(`/b/${board.shortId}/analytics`);
    };

    const handleAutomationsClick = () => {
        navigate(`/b/${board.shortId}/automations`);
    };

    const handleIntelligenceClick = () => {
        // Toggle AI chatbot
        const chatbotEvent = new CustomEvent('toggleAIChatbot');
        window.dispatchEvent(chatbotEvent);
    };

    useEffect(() => {
        if (boardId) {
            dispatch(fetchMembers({ type: 'board', id: boardId }));
            dispatch(setBoardId(boardId));
        }
    }, [dispatch, boardId]);

    const handleMemberInfo = (e, contentId) => {
        const target = e.currentTarget;
        const rect = target.getBoundingClientRect();
        const position = { top: rect.bottom, left: rect.left };
        dispatch(togglePopover({ contentId, position, targetId: target.id }));
    };

    const updateLastViewedBoard = async () => {
        try {

            await fetch(`${config.API_URI}/v1/boards/${boardId}/last-viewed`, {
                method: 'POST',
                headers: {

                    'Content-Type': 'application/json'
                },
                credentials: 'include',
            });
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
        }
    };

    useEffect(() => {
        if (boardId) {
            updateLastViewedBoard();
        }
    }, [boardId]);

    // Listen for chatbot state changes
    useEffect(() => {
        const handleChatbotStateChange = (event) => {
            setIsChatbotOpen(event.detail.isOpen);
        };

        window.addEventListener('chatbotStateChanged', handleChatbotStateChange);
        return () => {
            window.removeEventListener('chatbotStateChanged', handleChatbotStateChange);
        };
    }, []);
    // Global ESC-to-close: close Filter, Menu, and AI Assistant
    useEffect(() => {
        const handleEscKey = (e) => {
            if (e.key === 'Escape' || e.code === 'Escape') {
                // Close filter popover
                setShowFilter(false);
                // Close board menu
                setShowMenu(false);
                // Close AI assistant if open (uses global toggle event)
                if (isChatbotOpen) {
                    const chatbotToggle = new CustomEvent('toggleAIChatbot');
                    window.dispatchEvent(chatbotToggle);
                }
            }
        };

        window.addEventListener('keydown', handleEscKey);
        return () => window.removeEventListener('keydown', handleEscKey);
    }, [isChatbotOpen]);


    useOutsideClick(filterRef, () => setShowFilter(false), isShowFilter);
    useOutsideClick(viewMenuRef, () => setViewMenuOpen(false), isViewMenuOpen);
    // useOutsideClick(menuRef, () => setShowMenu(false), isShowMenu);

    // Decide which side the filter popover opens toward: left-anchored by
    // default so it opens to the right of the filter icon, but flip to
    // right-anchored if it would spill past the right edge of the viewport.
    // Measured after the panel mounts.
    useLayoutEffect(() => {
        if (!isShowFilter || !filterPopoverRef.current) return;
        const measure = () => {
            const el = filterPopoverRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            const margin = 8;
            // Left-anchored panel grows right; if its right edge overflows,
            // anchor right instead so it grows leftward. And vice-versa.
            if (rect.right > window.innerWidth - margin && filterAlign !== 'right') {
                setFilterAlign('right');
            } else if (rect.left < margin && filterAlign !== 'left') {
                setFilterAlign('left');
            }
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [isShowFilter, filterAlign]);

    const updateFilters = async (newFilters) => {
        setLoading(true);
        try {
            const response = await fetch(`${config.API_URI}/v1/users/me/filters`, {
                method: 'PUT',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ filters: newFilters, boardId })
            });

            if (!response.ok) {
                throw new Error('Failed to update filters');
            }
            const data = await response.json();
            setBoard(data.board || { actionLists: [] });
            setLoading(false);

        } catch (error) {
            console.error('Error updating filters:', error);
        }
    };

    const handleClearFilters = () => {
        const clearedFilters = { ...initialFiltersState, filteredStatus: false };
        updateFilters(clearedFilters);
        dispatch(setFilterQuery(clearedFilters));
    }


    return (
        <div className="zoobbe-workspace-navbar" ref={ref}>

            <NavbarLeft boardId={boardId} board={board} setBoard={setBoard} isReadOnly={isReadOnly} isAdmin={isAdmin} />

            <div className="zoobbe-navbar-right" >
                <div className="filter-content" ref={filterRef} >

                    <div
                        className={`cards-count${query.filteredStatus ? ' active' : ' active'}`}
                        style={{ paddingRight: isLoading ? '2px' : '4px', cursor: 'pointer' }}
                        data-tooltip-content="Cards"
                        data-tooltip-position="bottom"
                    >
                        <span className="material-symbols-outlined">
                            credit_card_gear
                        </span>
                        <span className='count-number'>
                            {(query.filteredStatus) ? cardCount : board.cardsCount}
                        </span>
                    </div>

                    <div
                        className="zoobbe-filters"
                        onClick={() => setShowFilter(!isShowFilter)}
                        data-popover-trigger>
                        {
                            query.filteredStatus && (
                                <div className="filter-indicator"></div>
                            )
                        }

                        <FilterList
                            tooltipContent={query.filteredStatus ? "Filters applied" : "Filters"}
                            tooltipPosition="bottom"
                            size={22} color='var(--white)'
                        />
                    </div>

                    {
                        isShowFilter && (
                            <div className={`filter-popover filter-popover--${filterAlign}`} ref={filterPopoverRef}>
                                <Filter
                                    board={board}
                                    setBoard={setBoard}
                                    isLoading={isLoading}
                                    setLoading={setLoading}
                                    setShowFilter={setShowFilter}
                                    cardCount={cardCount}
                                    setCardCount={setCardCount}
                                />
                            </div>
                        )
                    }
                </div>

                <div className={`board-view-switch${isViewMenuOpen ? ' open' : ''}`} ref={viewMenuRef}>
                    {(() => {
                        const ActiveIcon = (BOARD_VIEWS[viewMode] || BOARD_VIEWS.board).icon;
                        return (
                            <button
                                type="button"
                                className="board-view-switch__trigger"
                                onClick={() => setViewMenuOpen((o) => !o)}
                                data-tooltip-content="Switch view"
                                data-tooltip-position="bottom"
                            >
                                <ActiveIcon size={18} color='var(--white)' />
                                <KeyboardArrowDown size={16} color='var(--white)' />
                            </button>
                        );
                    })()}

                    {isViewMenuOpen && (
                        <div className="board-view-switch__menu">
                            {VIEW_ORDER.map((key) => {
                                const view = BOARD_VIEWS[key];
                                const Icon = view.icon;
                                return (
                                    <button
                                        key={key}
                                        type="button"
                                        className={`board-view-switch__item${viewMode === key ? ' active' : ''}`}
                                        onClick={() => { setViewMode(key); setViewMenuOpen(false); }}
                                    >
                                        <Icon size={16} color='currentColor' />
                                        <span>{view.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="zoobbe-members">
                    <MemberImage members={members} type={'board'} cardId={board.shortId} size={30} />
                    <MoreMembersCount members={members} boardId={board.shortId} uniqid={board.shortId} />
                </div>
                {!isReadOnly && canManageMembers && (
                    <div className="zoobbe-share" onClick={handleShareBoardModal}>
                        <PersonAdd size={18} color='var(--white)' tooltip={{ content: 'Share', position: 'bottom' }} />
                    </div>
                )}

                <div
                    className="zoobbe-automations"
                    onClick={handleAutomationsClick}
                    style={{ cursor: 'pointer' }}
                >
                    <Bolt
                        size={18}
                        color='var(--white)'
                        tooltip={{ content: 'Automations', position: 'bottom' }}
                    />
                </div>

                <div
                    className={`zoobbe-intelligence ${isChatbotOpen ? 'active' : ''}`}
                    onClick={handleIntelligenceClick}
                    style={{ cursor: 'pointer' }}
                >
                    <WandShine
                        size={18}
                        color={isChatbotOpen ? 'var(--brand-color)' : 'var(--white)'}
                        tooltip={{ content: isChatbotOpen ? 'Close AI Assistant' : 'Open AI Assistant', position: 'bottom' }}
                    />
                </div>

                <button
                    className="zoobbe-board-more"
                    onClick={() => setShowMenu(!isShowMenu)}
                    data-tooltip-content='Menu'
                    data-tooltip-position='bottom'
                    data-popover-trigger

                >
                    <MoreHoriz size={22} color='var(--white)' />

                </button>

                {
                    isShowMenu && (
                        <div ref={menuRef} style={{ position: 'absolute' }}>
                            <BoardMenu
                                boardId={board.shortId}
                                setShowMenu={setShowMenu}
                                board={board}
                                setBoard={setBoard}
                                isLoading={isLoading}
                                setLoading={setLoading}
                                isReadOnly={isReadOnly}
                                isAdmin={isAdmin}
                            />

                        </div>
                    )
                }
            </div>

        </div >


    );
});

export default Navbar;
